import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  ArrowUpDown, 
  FileText,
  Wallet, 
  Tags, 
  CreditCard, 
  Settings, 
  LogOut,
  Menu,
  X,
  Shield,
  Moon,
  Sun,
  ChevronsLeft,
  ChevronsRight,
  User,
  Building2
} from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { useThemeStore } from "../stores/themeStore";
import { useFinanceStore } from "../stores/finance-store";
import { getStoredKey } from "../lib/crypto";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/transactions", label: "Transações", icon: ArrowUpDown },
  { path: "/account-statement", label: "Extrato", icon: FileText },
  { path: "/accounts", label: "Contas", icon: Wallet },
  { path: "/categories", label: "Categorias", icon: Tags },
  { path: "/cards", label: "Cartões", icon: CreditCard },
  { path: "/settings", label: "Configurações", icon: Settings },
];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout, tenants, activeTenant, fetchTenants, switchTenant } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { clearData, fetchTransactions, fetchAccounts, fetchCategories, fetchCards } = useFinanceStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if crypto key exists, redirect to unlock if not
  useEffect(() => {
    const key = getStoredKey();
    if (!key && location.pathname !== "/unlock") {
      navigate("/unlock");
    }
  }, [location, navigate]);

  useEffect(() => {
    fetchTenants().catch(() => {});
  }, [fetchTenants]);
  
  const handleLogout = async () => {
    clearData();
    await logout();
    navigate("/login");
  };

  const handleTenantSwitch = async (environmentType) => {
    if (activeTenant?.environment_type === environmentType) return;

    clearData();
    const result = await switchTenant(environmentType);
    if (result.needsUnlock) {
      navigate("/unlock");
      return;
    }

    if (result.success) {
      await Promise.all([fetchTransactions(), fetchAccounts(), fetchCategories(), fetchCards()]);
    }
  };
  
  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);
  
  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-50">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-secondary" />
          <span className="font-bold text-lg font-heading">KawaFinance</span>
        </div>
        
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          data-testid="theme-toggle-mobile"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </header>
      
      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 bg-sidebar text-sidebar-foreground border-r border-sidebar-border z-[60] transition-all duration-300",
          "lg:translate-x-0",
          sidebarCollapsed && "lg:w-20",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
            <div className={cn("flex items-center gap-2 min-w-0", sidebarCollapsed && "lg:justify-center lg:flex-1")}>
              <Shield className="w-6 h-6 text-secondary" />
              <span className={cn("font-bold text-xl tracking-tight transition-opacity", sidebarCollapsed && "lg:hidden")}>
                KawaFinance
              </span>
            </div>
            <button
              onClick={() => setSidebarCollapsed((value) => !value)}
              className="hidden lg:inline-flex p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              title={sidebarCollapsed ? "Expandir sidebar" : "Recolher sidebar"}
              aria-label={sidebarCollapsed ? "Expandir sidebar" : "Recolher sidebar"}
              data-testid="sidebar-collapse-toggle"
            >
              {sidebarCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Environment Switch */}
          <div className={cn("px-4 py-3 border-b border-white/10", sidebarCollapsed && "lg:px-3")}>
            <div className={cn(
              "grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-white/10 p-1 shadow-inner",
              sidebarCollapsed && "lg:grid-cols-1"
            )}>
              {[
                { type: "personal", label: "Pessoal", icon: User },
                { type: "business", label: "Empresa", icon: Building2 },
              ].map((item) => {
                const Icon = item.icon;
                const tenant = tenants.find((entry) => entry.environment_type === item.type);
                const isActive = activeTenant?.environment_type === item.type;
                return (
                  <button
                    key={item.type}
                    type="button"
                    onClick={() => tenant && handleTenantSwitch(item.type)}
                    disabled={!tenant}
                    title={item.label}
                    aria-pressed={isActive}
                    className={cn(
                      "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors",
                      isActive
                        ? "border border-white/30 bg-white text-sidebar shadow-[0_6px_18px_rgba(0,0,0,0.14)]"
                        : "text-white/70 hover:bg-white/10 hover:text-white",
                      !tenant && "cursor-not-allowed opacity-50",
                      sidebarCollapsed && "lg:px-0"
                    )}
                    data-testid={`tenant-switch-${item.type}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className={cn(sidebarCollapsed && "lg:hidden")}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* User Info */}
          <div className={cn("p-4 border-b border-white/10", sidebarCollapsed && "lg:px-3")}>
            <div className={cn("flex items-center gap-3", sidebarCollapsed && "lg:justify-center")}>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-white font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className={cn("flex-1 min-w-0", sidebarCollapsed && "lg:hidden")}>
                <p className="font-medium truncate">{user?.name}</p>
                <div className="flex items-center gap-1 text-xs text-secondary">
                  <Shield className="w-3 h-3" />
                  <span>E2EE Ativo</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className={cn("flex-1 p-4 space-y-1 overflow-y-auto", sidebarCollapsed && "lg:px-3")}>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200",
                    sidebarCollapsed && "lg:justify-center lg:px-2",
                    isActive
                      ? "bg-white text-sidebar font-medium"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  )
                }
                title={item.label}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5" />
                <span className={cn(sidebarCollapsed && "lg:hidden")}>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          
          {/* Bottom Actions */}
          <div className={cn("p-4 border-t border-white/10 flex flex-row items-center justify-around gap-2 pb-[calc(1rem+env(safe-area-inset-bottom))]", sidebarCollapsed && "lg:flex-col lg:px-3")}>
            <button
              onClick={toggleTheme}
              className="flex-1 flex justify-center items-center gap-2 p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="theme-toggle"
              title={theme === "dark" ? "Modo Claro" : "Modo Escuro"}
            >
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            <Button
              variant="ghost"
              className="flex-1 justify-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Sair"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className={cn("min-h-screen pt-16 lg:pt-0 transition-all duration-300", sidebarCollapsed ? "lg:ml-20" : "lg:ml-64")}>
        <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t border-border flex flex-row items-center justify-around px-2 z-50">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-1 p-2 rounded-lg transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs">{item.label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
